import { dbManager } from './db_manager';
import { getLocalISOString } from '../utils/timezoneUtils';

export type StatField = 'pages_read' | 'words_written' | 'pronunciation_count' | 'cards_reviewed' | 'xp_earned' | 'books_read' | 'active_voice_duration' | 'total_quizzes_attempted' | 'estimated_minutes' | 'accuracy_rate' | 'has_studied' | 'listening_count';

export interface DailyLog {
  activity_date: string;
  pages_read: number;
  words_written: number;
  pronunciation_count: number;
  cards_reviewed: number;
  books_read: number;
  active_voice_duration: number;
  total_quizzes_attempted?: number;
  listening_count?: number;
}

const BUFFER_KEY = 'libritui_stats_buffer';
const RETRY_QUEUE_KEY = 'libritui_stats_retry_queue';
const LAST_SYNC_DATE_KEY = 'libritui_stats_last_sync_date';

const syncLocks: Record<string, boolean> = {};
let flushTimeout: any = null;
let isFlushing = false;
let currentUserId: string | undefined;

export const statsService = {
  /**
   * Initializes listeners for app lifecycle and visibility.
   */
  init() {
    if (typeof window === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      } else if (document.visibilityState === 'visible') {
        this.processRetryQueue();
      }
    };

    const handleBeforeUnload = () => {
      this.flush();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Attempt to process queue on startup after a safe delay
    setTimeout(() => {
        this.processRetryQueue();
        this.flush(); // Flush any leftover buffer from previous sessions
    }, 5000);
  },

  /**
   * Increments a specific statistic for the current day with batching and retry logic.
   */
  async increment(field: StatField, userId?: string, amount: number = 1, meta?: any): Promise<void> {
    const today = getLocalISOString().split('T')[0];
    const langCode = meta?.langCode || 'all';

    if (userId && userId !== 'undefined' && userId !== 'null' && userId !== 'guest_user') {
        currentUserId = userId;
    }

    // 1. Instant Optimistic Signal: Notify UI to update immediately with local data
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('libritui-stats-updated', { 
          detail: { field, amount, date: today, meta, langCode } 
      }));
    }

    // 2. Batching Strategy (Buffering)
    const buffer = this._getBuffer();
    const bufferKey = `${field}::${langCode}`;
    buffer[bufferKey] = (buffer[bufferKey] || 0) + amount;
    this._saveBuffer(buffer);
    
    // Set dirty flag to notify StatisticsPage that data has changed
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('libritui_stats_dirty_FLAG', 'true');
    }

    // GUARD: Only attempt to flush to server if userId is loaded and valid (not guest/undefined)
    if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest_user') {
      return;
    }

    // 3. Debounced Sync (1 second)
    // We wait 1s after the LAST increment to send the batch to D1
    if (flushTimeout) clearTimeout(flushTimeout);
    
    flushTimeout = setTimeout(() => {
      this.flush(userId);
    }, 1000);
  },

  /**
   * Forces a sync of all buffered statistics.
   */
  async flush(explicitUserId?: string): Promise<void> {
    if (isFlushing) return;
    try {
      isFlushing = true;
      let userId = explicitUserId || currentUserId;
      
      // If we still don't have a userId, and we're not in a beforeunload context, we can try to fetch it async
      if (!userId) {
        // If the browser is unloading, we cannot use 'await' or the execution context will be destroyed
        // We just skip the flush in this edge case and let the next session flush the buffer
        isFlushing = false;
        return;
      }

      if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest_user') {
        isFlushing = false;
        return;
      }

      const buffer = this._getBuffer();
      const fields = Object.keys(buffer) as StatField[];
      if (fields.length === 0) {
        isFlushing = false;
        return;
      }

      const promises = [];
      for (const bufferKey of fields) {
        const amount = buffer[bufferKey];
        const [field, langCode] = bufferKey.split('::');
        if (amount > 0) {
          promises.push(
            this._sendImmediately(field as StatField, amount, userId, langCode, false).then(success => {
              if (success) {
                const currentBuffer = this._getBuffer();
                delete currentBuffer[bufferKey];
                this._saveBuffer(currentBuffer);
              }
            })
          );
        }
      }
      
      // We don't await the promises if we are in a beforeunload context to prevent the browser 
      // from pausing execution and killing the remaining loop iterations. The fetch calls have 
      // keepalive: true, so they will complete in the background.
      Promise.all(promises).catch(err => console.warn('[Stats] Flush promises error:', err));

      
      // Notify UI that background sync is complete
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('libritui-stats-synced'));
      }
    } catch (err) {
      console.error('[Stats] Error during flush:', err);
    } finally {
      isFlushing = false;
    }
  },

  /**
   * Internal function to send immediately to D1 or add to retry queue.
   */
  async _sendImmediately(field: StatField, amount: number, userId: string, langCode: string = 'all', isRetry = false): Promise<boolean> {      if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest_user') return false;
      const lockKey = `${field}::${langCode}`;
      if (syncLocks[lockKey] && !isRetry) return false;
      
      try {
        syncLocks[lockKey] = true;
        
        const localDate = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
        
        // Update primary table: user_daily_logs
        await dbManager.execute({
          action: 'increment_stat',
          table: 'user_daily_logs',
          user_id: userId,
          data: { field, amount: amount.toString(), lang_code: langCode, local_date: localDate }
        });
        
        const { cacheManager } = await import('./cacheManager');
        cacheManager.clear(`stats_logs_${userId}_30`);
        cacheManager.clear(`stats_logs_${userId}_60`);

        return true;
      } catch (err) {
        console.warn(`[Stats] Sync failed for ${field}:`, err);
        if (!isRetry) {
          this._addToRetryQueue(field, amount, userId, langCode);
        }
        return false;
      } finally {
        syncLocks[lockKey] = false;
      }
  },

  /**
   * Retries sending failed requests stored in the queue.
   */
  async processRetryQueue(): Promise<void> {
    const queue = this._getRetryQueue();
    if (queue.length === 0) return;

    console.log(`[Stats] Processing retry queue with ${queue.length} items.`);
    const remainingQueue = [];

    for (const item of queue) {
      const success = await this._sendImmediately(item.field, item.amount, item.userId, item.langCode || 'all', true);
      if (!success) {
        remainingQueue.push(item);
      }
    }

    this._saveRetryQueue(remainingQueue);
  },

  // Helper methods for localStorage
  _getBuffer(): Record<string, number> {
    try {
      return JSON.parse(localStorage.getItem(BUFFER_KEY) || '{}');
    } catch { return {}; }
  },
  _saveBuffer(buffer: Record<string, number>) {
    localStorage.setItem(BUFFER_KEY, JSON.stringify(buffer));
  },
  _getRetryQueue(): any[] {
    try {
      return JSON.parse(localStorage.getItem(RETRY_QUEUE_KEY) || '[]');
    } catch { return []; }
  },
  _saveRetryQueue(queue: any[]) {
    localStorage.setItem(RETRY_QUEUE_KEY, JSON.stringify(queue));
  },
  _addToRetryQueue(field: StatField, amount: number, userId: string, langCode: string = 'all') {
    const queue = this._getRetryQueue();
    // Keep max 50 items in queue to prevent localstorage bloat
    if (queue.length > 50) queue.shift();
    queue.push({ field, amount, userId, langCode, timestamp: Date.now() });
    this._saveRetryQueue(queue);
  },

  setLangFilter(langCode: string) {
    (this as any)._currentLangFilter = langCode;
  },

  async getDashboardStats(userId: string, langCode: string, timeframe: string): Promise<any> {
    if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest_user') return null;
    try {
      const results = await dbManager.execute({
        action: 'get_dashboard_stats',
        user_id: userId,
        data: { lang_code: langCode, timeframe }
      });
      return results;
    } catch (error) {
      console.error('Failed to get dashboard stats:', error);
      return null;
    }
  },


  async getDailyLogs(userId: string, limit: number = 30): Promise<DailyLog[]> {
    if (!userId || userId === 'undefined' || userId === 'null' || userId === 'guest_user') return [];
    try {
      const langCode = (this as any)._currentLangFilter || 'all';
      const cacheKey = `stats_logs_${userId}_${limit}_${langCode}`;
      
      // Implementar Cache de Curto Prazo em Memoria (Front-end) 10s
      if (!(this as any)._shortTermLogsCache) (this as any)._shortTermLogsCache = new Map();
      const shortCacheMap = (this as any)._shortTermLogsCache;
      const cached = shortCacheMap.get(cacheKey);
      if (cached && Date.now() - cached.time < 10000) {
          return cached.data;
      }

      const fetchFromD1 = async () => {
        const results = await dbManager.execute({
          action: 'get_daily_logs',
          table: 'user_daily_logs',
          user_id: userId,
          data: { limit: limit.toString(), lang_code: (this as any)._currentLangFilter || 'all' }
        });
        return Array.isArray(results) ? [...results] : [];
      };

      const baseLogs: any = await fetchFromD1();
      const logs = Array.isArray(baseLogs) ? [...baseLogs] : [];
      
      // Merge optimistic buffer and retry queue
      const buffer = this._getBuffer();
      const retryQueue = this._getRetryQueue();
      const today = getLocalISOString().split('T')[0];
      
      const fields: StatField[] = ['cards_reviewed', 'pages_read', 'words_written', 'pronunciation_count', 'books_read', 'active_voice_duration', 'total_quizzes_attempted', 'listening_count'];
      
      const todayLogIndex = logs.findIndex(l => l.activity_date === today);
      let todayLog = todayLogIndex !== -1 ? logs[todayLogIndex] : {
          activity_date: today,
          cards_reviewed: 0,
          pages_read: 0,
          words_written: 0,
          pronunciation_count: 0,
          books_read: 0,
          active_voice_duration: 0,
          total_quizzes_attempted: 0,
          listening_count: 0
      };

      let changed = false;

      for (const field of fields) {
        let pending = (buffer[field] || 0);
        retryQueue.forEach((q: any) => { if (q.field === field) pending += q.amount; });
        
        let dbVal = todayLog[field] || 0;
        let totalVal = dbVal + pending;

        const watermarkKey = `libritui_daily_watermark_${userId}_${today}_${field}`;
        const savedWatermark = parseInt(localStorage.getItem(watermarkKey) || '0', 10);
        
        totalVal = Math.max(totalVal, savedWatermark);
        if (totalVal > savedWatermark) {
           localStorage.setItem(watermarkKey, totalVal.toString());
        }

        if (totalVal > 0) {
           todayLog[field] = totalVal;
           changed = true;
        }
      }

      if (changed) {
         if (todayLogIndex !== -1) {
             logs[todayLogIndex] = todayLog;
         } else {
             logs.unshift(todayLog);
         }
      }
      
      if (logs && logs.length > 0) {
          shortCacheMap.set(cacheKey, { time: Date.now(), data: logs });
      }
      return logs;
    } catch (err) {
      console.error('[Stats] Error fetching daily logs:', err);
      return [];
    }
  },

  /**
   * Alias for getDailyLogs as used in StatisticsPage
   */
  async getLogs(limit: number = 30): Promise<DailyLog[]> {
    try {
      const session = await dbManager.execute({ action: 'select', table: 'profiles' }).catch(() => null);
      if (!session) return [];
      
      // Since dbManager handles session internally if user_id is missing, 
      // we can just call getDailyLogs without explicit userId if we want,
      // but let's be explicit if possible.
      const { data: { session: authSession } } = await (await import('./supabaseClient')).supabase.auth.getSession();
      if (!authSession?.user?.id) return [];
      
      return this.getDailyLogs(authSession.user.id, limit);
    } catch {
      return [];
    }
  },

  /**
   * Calculates improvement for a specific metric
   */
  calculateImprovement(logs: DailyLog[], metric: StatField) {
    if (!logs || logs.length === 0) return { current: 0, improvement: 0 };
    
    // Current period (last 7 days)
    const currentPeriod = logs.slice(0, 7);
    const currentTotal = currentPeriod.reduce((acc, log) => acc + (log[metric] || 0), 0);
    
    // Previous period (7 to 14 days ago)
    const previousPeriod = logs.slice(7, 14);
    const previousTotal = previousPeriod.reduce((acc, log) => acc + (log[metric] || 0), 0);
    
    let improvement = 0;
    if (previousTotal === 0) {
      improvement = currentTotal > 0 ? 100 : 0;
    } else {
      improvement = Math.round(((currentTotal - previousTotal) / previousTotal) * 100);
    }
    
    return { current: currentTotal, improvement };
  },

  /**
   * Calculates statistics for a period (daily, weekly, monthly) 
   * and returns comparison with the previous period.
   */
  async getStatsComparison(userId: string) {
    const logs = await this.getDailyLogs(userId, 60); 
    
    const today = getLocalISOString().split('T')[0];
    const yesterday = getLocalISOString(Date.now() - 86400000).split('T')[0];

    const todayLog = logs.find(l => l.activity_date === today);
    const yesterdayLog = logs.find(l => l.activity_date === yesterday);

    // Helper to calculate total for a range of days
    const sumRange = (start: number, end: number) => {
      const range = logs.slice(start, end);
      return {
        pages: range.reduce((acc, l) => acc + (l.pages_read || 0), 0),
        writing: range.reduce((acc, l) => acc + (l.words_written || 0), 0),
        pronunciation: range.reduce((acc, l) => acc + (l.pronunciation_count || 0), 0),
        flashcards: range.reduce((acc, l) => acc + (l.cards_reviewed || 0), 0),
        total: range.reduce((acc, l) => acc + (l.pages_read || 0) + (l.words_written || 0) + (l.pronunciation_count || 0) + (l.cards_reviewed || 0), 0),
      };
    };

    const last7Days = sumRange(0, 7);
    const previous7Days = sumRange(7, 14);

    const last30Days = sumRange(0, 30);
    const previous30Days = sumRange(30, 60);

    const calculateImprovement = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    };

      const finalData = {
        today: todayLog || { pages_read: 0, words_written: 0, pronunciation_count: 0, cards_reviewed: 0 },
        weekly: {
          current: last7Days,
          improvement: {
            pages: calculateImprovement(last7Days.pages, previous7Days.pages),
            writing: calculateImprovement(last7Days.writing, previous7Days.writing),
            pronunciation: calculateImprovement(last7Days.pronunciation, previous7Days.pronunciation),
            flashcards: calculateImprovement(last7Days.flashcards, previous7Days.flashcards),
          }
        },
        monthly: {
          current: last30Days,
          improvement: {
            pages: calculateImprovement(last30Days.pages, previous30Days.pages),
            writing: calculateImprovement(last30Days.writing, previous30Days.writing),
            pronunciation: calculateImprovement(last30Days.pronunciation, previous30Days.pronunciation),
            flashcards: calculateImprovement(last30Days.flashcards, previous30Days.flashcards),
          }
        },
        chartData: logs.slice(0, 7).reverse().map(l => ({
          date: l.activity_date.split('-').slice(1).join('/'), // MM/DD
          total: (l.pages_read || 0) + (l.words_written || 0) + (l.pronunciation_count || 0) + (l.cards_reviewed || 0)
        }))
      };

      return finalData;
  }
};
